import React, { useContext, useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Checkbox,
  IconButton,
  Link,
  Popover,
  SelectionControlLabel,
  Tile,
  TileContent,
  TileGroup,
  TileStateIndicator,
  Typography
} from '@one-thd/sui-atomic-components';
import { Info } from '@one-thd/sui-icons';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  HAUL_AWAY_TEXT,
  INSTALL_TEXT,
  PROTECTION_TEXT,
  PROTECTION_DETAILS_URL,
  POPOVER_TEXT,
  INSTALL_OPTIONS,
  INSTALL_ITEM_CONFIGURED,
  INSTALL_TWO_STEP,
  INSTALL_TWO_STEP_TITLE,
  INSTALL_TWO_STEP_LINE2,
  INSTALL_FOR_ME,
  INSTALL_FREE,
  INSTALL_SELF,
  INSTALL_NO_KIT_DESCRIPTION,
  INSTALL_PARTS_WARRANTY_NOTE,
  INSTALL_REQUIRED_PARTS_TITLE,
  DEFAULT_HAUL_AWAY_LABEL,
  HAUL_AWAY_DISPLAY_LABELS
} from '../../constants';
import {
  getInstallationDetails,
  getInstallPricing,
  getDescription,
  getDishwasherInstallLinks
} from '../../utils';

const OPTIONS_MAP = {
  protection: { title: PROTECTION_TEXT, popover: POPOVER_TEXT.PROTECTION },
  installation: { title: INSTALL_TEXT, popover: POPOVER_TEXT.INSTALLATION },
  haulAway: { title: HAUL_AWAY_TEXT, popover: POPOVER_TEXT.HAUL_AWAY_W_DOOR },
};

const DECLINE_OPTIONS = {
  protection: { key: 'decline', label: 'Decline Plan', price: null, isDecline: true },
};

const EMPTY_INSTALL_DATA = { details: null, pricing: null, dishwasherLinks: [] };

const formatPrice = (price) => {
  if (price == null) return '';
  return price === 0 ? 'FREE' : `$${Number(price).toFixed(2)}`;
};

const getInstallationOptions = (availableItems) => {
  const details = getInstallationDetails({ addOns: availableItems });
  const pricing = getInstallPricing(availableItems);
  const isHookup = details?.hasHookup;

  return [
    {
      key: INSTALL_OPTIONS.ACCEPT,
      label: isHookup ? INSTALL_FREE : INSTALL_FOR_ME,
      description: getDescription(pricing.hasRequiredParts, { isAccept: true, isHookup }),
      price: formatPrice(pricing.totalInstallPrice),
    },
    {
      key: INSTALL_OPTIONS.DECLINE,
      label: INSTALL_SELF,
      description: getDescription(pricing.hasRequiredParts, { isAccept: false }),
      price: pricing.hasRequiredParts ? formatPrice(pricing.standardKitPrice) : null,
    },
  ];
};

const getProtectionOptions = (availableItems) => {
  const plans = availableItems
    .filter((item) => !!item?.termLength)
    .sort((a, b) => b.termLength - a.termLength)
    .map((item) => ({
      key: item.configAttr || `term-${item.termLength}`,
      label: `${item.termLength}-Year Plan`,
      price: formatPrice(Number(item.price || 0)),
    }));

  return [...plans, DECLINE_OPTIONS.protection];
};

// the default haul away option is pinned first so it reads as the primary choice.
const getHaulAwayOptions = (availableItems) => {
  const options = availableItems.map((item) => ({
    key: item.configAttr || item.description,
    label: HAUL_AWAY_DISPLAY_LABELS[item.configAttr] || item.description,
    price: formatPrice(Number(item.price || 0)),
  }));
  const defaultIndex = options.findIndex((option) => option.label === DEFAULT_HAUL_AWAY_LABEL);

  if (defaultIndex > 0) {
    const [defaultOption] = options.splice(defaultIndex, 1);
    options.unshift(defaultOption);
  }

  return options;
};

const getDefaultOptions = (availableItems) => availableItems.map((item) => ({
  key: item.configAttr || item.description,
  label: item.description,
  price: formatPrice(Number(item.price || 0)),
}));

const OPTION_BUILDERS = {
  installation: getInstallationOptions,
  protection: getProtectionOptions,
  haulAway: getHaulAwayOptions,
};

const normalizeOptions = (type, availableItems) => {
  if (!availableItems?.length) return [];

  const buildOptions = OPTION_BUILDERS[type] || getDefaultOptions;
  return buildOptions(availableItems);
};

const getSelectedKey = (type, userSelection, options) => {
  if (!userSelection) return null;

  if (type === 'protection') {
    if (!userSelection?.termLength) return 'decline';
    return options.find((o) =>
      !o.isDecline
      && (o.key === userSelection.configAttr || o.key === `term-${userSelection.termLength}`)
    )?.key || null;
  }

  if (Array.isArray(userSelection)) {
    const hasReal = userSelection.some((item) =>
      item?.installation || item?.configAttr === 'Hookup' || item?.price > 0
    );
    if (!hasReal) return 'decline';
    return userSelection[0]?.configAttr || userSelection[0]?.description || null;
  }

  return null;
};

const getPopoverText = (type, availableItems) => {
  if (type !== 'haulAway') return OPTIONS_MAP[type].popover;

  const hasDoorSwing = availableItems?.some(
    (item) => item?.description?.trim() === 'Change Door Swing Direction'
  );
  return hasDoorSwing ? POPOVER_TEXT.HAUL_AWAY_W_DOOR : POPOVER_TEXT.HAUL_AWAY_WO_DOOR;
};

const getInstallData = (addOns) => ({
  details: getInstallationDetails({ addOns }),
  pricing: getInstallPricing(addOns),
  dishwasherLinks: getDishwasherInstallLinks(addOns)
});

// which install key a controlled userSelection maps to, plus the parts it implies.
// a null partConfigAttrs means "leave the checked-parts state alone".
const getInstallationSyncState = (userSelection) => {
  const hasAcceptSignal = userSelection.some(
    (item) => item?.installation || item?.configAttr?.toUpperCase() === 'HOOKUP'
  );

  return {
    selectedKey: hasAcceptSignal ? INSTALL_OPTIONS.ACCEPT : INSTALL_OPTIONS.DECLINE,
    partConfigAttrs: hasAcceptSignal
      ? null
      : userSelection.filter((item) => item?.type === 'Part').map((item) => item.configAttr)
  };
};

const getMultiSelectKeys = (userSelection) => userSelection
  .map((item) => item?.configAttr || item?.description)
  .filter(Boolean);

const getMultiSelectDisplayPrice = (selectedKeys, findAvailableItem) => {
  if (selectedKeys.length === 0) return '';

  const totalCents = selectedKeys.reduce((total, key) => {
    const item = findAvailableItem(key);
    return total + Math.round(Number(item?.price || 0) * 100);
  }, 0);

  return formatPrice(totalCents / 100);
};

const getCheckedPartItems = ({
  isDeclineSelected, hasRequiredParts, installPricing, selectedPartConfigAttrs
}) => {
  if (!isDeclineSelected || !hasRequiredParts) return [];

  return installPricing.installPartItems.filter(
    (item) => selectedPartConfigAttrs.includes(item.configAttr)
  );
};

const getTileGroupProps = ({
  title, isInstallation, isMultiSelect, selectedKey, selectedKeys
}) => ({
  'aria-label': `${title} options`,
  value: isMultiSelect ? selectedKeys : selectedKey,
  disableExclusive: isMultiSelect,
  ...(isInstallation
    ? { grow: true }
    : { className: 'sui-grid sui-grid-cols-1 sm:sui-grid-cols-3 sui-gap-2' }
  )
});

export const TileButton = ({
  type,
  onSelectionChange,
  userSelection,
  availableItems,
  isBopisAndPickup
}) => {
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const isInstallation = type === 'installation';
  const { title } = OPTIONS_MAP[type];
  const popoverText = useMemo(() => getPopoverText(type, availableItems), [type, availableItems]);
  const addOns = availableItems || [];
  const {
    details: installDetails,
    pricing: installPricing,
    dishwasherLinks
  } = isInstallation ? getInstallData(addOns) : EMPTY_INSTALL_DATA;
  const options = useMemo(() => normalizeOptions(type, availableItems), [type, availableItems]);
  // configAttrs of every required part, used as the default "all checked" state for self-install.
  const requiredPartConfigAttrs = useMemo(
    () => (installPricing?.installPartItems || []).map((item) => item.configAttr),
    [installPricing]
  );

  const isMultiSelect = type === 'haulAway';
  const defaultHaulAwayKey = useMemo(
    () => options.find((option) => option.label === DEFAULT_HAUL_AWAY_LABEL)?.key || null,
    [options]
  );
  const defaultSelectedKeys = useMemo(
    () => (isMultiSelect && defaultHaulAwayKey ? [defaultHaulAwayKey] : []),
    [isMultiSelect, defaultHaulAwayKey]
  );
  const defaultKey = isInstallation ? INSTALL_OPTIONS.ACCEPT : (options[0]?.key || 'decline');
  const [selectedKey, setSelectedKey] = useState(isMultiSelect ? null : defaultKey);
  const [selectedKeys, setSelectedKeys] = useState(defaultSelectedKeys);
  // which required parts are currently checked while self-install is selected.
  const [selectedPartConfigAttrs, setSelectedPartConfigAttrs] = useState(requiredPartConfigAttrs);
  const hasInitializedMultiSelect = useRef(false);

  const findAvailableItem = (key) => {
    if (type === 'protection') {
      return availableItems.find((i) => i.configAttr === key || `term-${i.termLength}` === key);
    }
    return availableItems.find((i) => (i.configAttr || i.description) === key);
  };

  useEffect(() => {
    if (isMultiSelect) {
      hasInitializedMultiSelect.current = false;
    }
  }, [type, isMultiSelect]);

  useEffect(() => {
    if (isInstallation) {
      if (!userSelection) return;
      // keep the checked-parts state in sync with a controlled userSelection prop
      // (e.g. after the parent persists a deselected part) instead of only tracking local clicks.
      const { selectedKey: syncedKey, partConfigAttrs } = getInstallationSyncState(userSelection);
      setSelectedKey(syncedKey);
      if (partConfigAttrs) setSelectedPartConfigAttrs(partConfigAttrs);
      return;
    }

    if (userSelection == null) {
      // only multi-select seeds a default when the parent has no selection yet.
      const shouldSeedDefaults = isMultiSelect
        && !hasInitializedMultiSelect.current
        && defaultSelectedKeys.length > 0;
      if (shouldSeedDefaults) {
        setSelectedKeys(defaultSelectedKeys);
        hasInitializedMultiSelect.current = true;
      }
      return;
    }

    if (isMultiSelect) {
      setSelectedKeys(getMultiSelectKeys(userSelection));
      hasInitializedMultiSelect.current = true;
      return;
    }

    setSelectedKey(getSelectedKey(type, userSelection, options) || defaultKey);
  }, [type, userSelection, options, defaultKey, isMultiSelect, isInstallation, defaultSelectedKeys]);

  useEffect(() => {
    if (!onSelectionChange || userSelection) return;

    if (isInstallation) {
      const acceptValue = availableItems.filter(
        (item) => item?.configAttr !== INSTALL_ITEM_CONFIGURED.configAttr
      );
      onSelectionChange(acceptValue);
      return;
    }

    if (isMultiSelect && defaultHaulAwayKey) {
      const defaultItem = findAvailableItem(defaultHaulAwayKey);
      onSelectionChange(defaultItem ? [defaultItem] : null);
      return;
    }

    if (defaultKey !== 'decline') {
      onSelectionChange(findAvailableItem(defaultKey) || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // derived state for the self-install required-parts checklist (which parts are checked,
  // their combined price, and singular/plural copy) — recomputed each render from selectedPartConfigAttrs.
  const isDeclineSelected = isInstallation && selectedKey === INSTALL_OPTIONS.DECLINE;
  const hasRequiredParts = !!installPricing?.hasRequiredParts;
  const checkedPartItems = getCheckedPartItems({
    isDeclineSelected, hasRequiredParts, installPricing, selectedPartConfigAttrs
  });
  const checkedPartsTotal = checkedPartItems.reduce((sum, item) => sum + Number(item?.price || 0), 0);
  const isPluralParts = (installPricing?.installPartItems || []).length > 1;

  const getDisplayPrice = () => {
    if (isMultiSelect) return getMultiSelectDisplayPrice(selectedKeys, findAvailableItem);
    // header price reflects only the currently checked required parts, not the full kit total.
    if (isDeclineSelected && hasRequiredParts) {
      return checkedPartsTotal > 0 ? formatPrice(checkedPartsTotal) : '--';
    }
    return options.find((option) => option.key === selectedKey)?.price || '';
  };

  const displayPrice = getDisplayPrice();
  const textColor = isBopisAndPickup ? 'inactive' : undefined;

  // when self-install is the active selection, the tile's own description/price must
  // reflect live part-checkbox state rather than the static normalizeOptions() output.
  const getTileDisplay = (option) => {
    const showsLiveParts = isInstallation
      && option.key === INSTALL_OPTIONS.DECLINE
      && isDeclineSelected
      && hasRequiredParts;

    if (!showsLiveParts) {
      return { description: option.description, price: option.price };
    }
    if (checkedPartItems.length === 0) {
      return { description: INSTALL_NO_KIT_DESCRIPTION, price: null };
    }
    return { description: option.description, price: formatPrice(checkedPartsTotal) };
  };

  const emitInstallationSelection = (key) => {
    if (!onSelectionChange) return;

    const installationValue = key === INSTALL_OPTIONS.ACCEPT
      ? availableItems.filter((item) => item?.configAttr !== INSTALL_ITEM_CONFIGURED.configAttr)
      : [INSTALL_ITEM_CONFIGURED, ...installPricing.installPartItems];
    onSelectionChange(installationValue);
  };

  const handleInstallationClick = (key) => {
    if (key === selectedKey) return;

    setSelectedKey(key);
    // every fresh switch into self-install defaults all required parts back to checked.
    if (key === INSTALL_OPTIONS.DECLINE) {
      setSelectedPartConfigAttrs(requiredPartConfigAttrs);
    }
    emitInstallationSelection(key);
  };

  const handleMultiSelectClick = (key) => {
    if (key === 'decline') {
      setSelectedKeys([]);
      if (onSelectionChange) onSelectionChange(null);
      return;
    }

    const newKeys = selectedKeys.includes(key)
      ? selectedKeys.filter((k) => k !== key)
      : [...selectedKeys, key];
    setSelectedKeys(newKeys);

    if (!onSelectionChange) return;
    const selectedItems = newKeys.map((k) => findAvailableItem(k)).filter(Boolean);
    onSelectionChange(selectedItems.length > 0 ? selectedItems : null);
  };

  const handleSingleSelectClick = (key) => {
    setSelectedKey(key);
    if (onSelectionChange) {
      onSelectionChange(key === 'decline' ? null : findAvailableItem(key));
    }
  };

  const handleTileClick = (key) => {
    if (isBopisAndPickup) return;

    if (isInstallation) {
      handleInstallationClick(key);
    } else if (isMultiSelect) {
      handleMultiSelectClick(key);
    } else {
      handleSingleSelectClick(key);
    }
  };

  // toggles one required part on/off and reports the updated selection (Configured marker + checked parts).
  const handlePartToggle = (configAttr) => {
    const nextSelectedParts = selectedPartConfigAttrs.includes(configAttr)
      ? selectedPartConfigAttrs.filter((attr) => attr !== configAttr)
      : [...selectedPartConfigAttrs, configAttr];
    setSelectedPartConfigAttrs(nextSelectedParts);
    if (onSelectionChange) {
      const checkedParts = installPricing.installPartItems.filter(
        (item) => nextSelectedParts.includes(item.configAttr)
      );
      onSelectionChange([INSTALL_ITEM_CONFIGURED, ...checkedParts]);
    }
  };

  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const isPopoverOpen = Boolean(popoverAnchor);
  const popoverId = isPopoverOpen ? `${type}-info-popover` : undefined;

  const handleInfoClick = (event) => setPopoverAnchor(event.currentTarget);
  const handleInfoClose = () => setPopoverAnchor(null);

  return (
    <div className="sui-w-full sui-flex sui-flex-col sui-gap-2">
      <div className="sui-flex sui-justify-between sui-items-center">
        <div className="sui-flex sui-items-center sui-gap-1">
          <Typography variant="h5" color={textColor}>{title}</Typography>
          <IconButton aria-describedby={popoverId} onClick={handleInfoClick} icon={Info} color="info"/>
          <Popover
            id={popoverId}
            open={isPopoverOpen}
            anchorEl={popoverAnchor}
            onClose={handleInfoClose}
          >
            <Typography variant="body-xs">
              {popoverText}
            </Typography>
            {type === 'protection' && (
              <Link href={PROTECTION_DETAILS_URL} target="_blank" variant="body-xs">
                Plan Details
              </Link>
            )}
            {dishwasherLinks.length > 0 && (
              <div className="sui-flex sui-flex-row sui-flex-wrap sui-gap-4 sui-mt-3">
                {dishwasherLinks.map((installLink) => (
                  <Link key={installLink.href} href={installLink.href} target="_blank" variant="body-xs">
                    {installLink.label}
                  </Link>
                ))}
              </div>
            )}
          </Popover>
        </div>
        <Typography variant="h5" color={textColor}>
          {displayPrice}
        </Typography>
      </div>
      {isInstallation && installDetails?.hasTwoStepInstallation && (
        <Alert status="warning">
          <div className="sui-flex sui-flex-col sui-pr-3">
            <Typography variant="body-sm" weight="bold">{INSTALL_TWO_STEP_TITLE}</Typography>
            <Typography variant="body-sm" weight="regular" className="sui-mt-2">{INSTALL_TWO_STEP} {INSTALL_TWO_STEP_LINE2}</Typography>
          </div>
        </Alert>
      )}
      {type === 'haulAway' ? (
        <Typography variant="body-sm" color={textColor} className="sui-whitespace-nowrap">
          * Select all that apply
        </Typography>
      ) : null}
      <TileGroup {...getTileGroupProps({
        title, isInstallation, isMultiSelect, selectedKey, selectedKeys
      })}
      >
        {options.map((option) => {
          const isTileSelected = isMultiSelect
            ? selectedKeys.includes(option.key)
            : option.key === selectedKey;
          const tileWeight = isTileSelected ? 'bold' : 'regular';
          const { description: optionDescription, price: optionPrice } = getTileDisplay(option);

          return (
            <Tile
              key={option.key}
              value={option.key}
              aria-label={option.label}
              disabled={isBopisAndPickup}
              onClick={isBopisAndPickup ? undefined : () => handleTileClick(option.key)}
            >
              <TileContent alignItems="start">
                <div className="sui-flex sui-flex-col sui-gap-2 sui-flex-1">
                  {isMobile ? (
                    <div className="sui-flex sui-justify-between sui-items-center">
                      <Typography variant="body-base" weight={tileWeight} color={textColor}>{option.label}</Typography>
                      {!option.isDecline && optionPrice && (
                        <Typography variant="body-base" weight={tileWeight} color={textColor}>{optionPrice}</Typography>
                      )}
                    </div>
                  ) : (
                    <Typography variant="body-base" weight={tileWeight} color={textColor}>{option.label}</Typography>
                  )}
                  {optionDescription && (
                    <Typography variant="body-sm" color={textColor}>{optionDescription}</Typography>
                  )}
                  {option.isDecline ? (
                    !isMobile && <Typography variant="body-sm" color={textColor}>Not Recommended</Typography>
                  ) : !isMobile && optionPrice && (
                    <Typography variant="body-base" weight={tileWeight} color={textColor}>{optionPrice}</Typography>
                  )}
                </div>
                {!isMobile && <TileStateIndicator position="top" />}
              </TileContent>
            </Tile>
          );
        })}
      </TileGroup>
      {/* required-parts checklist, expanded only while self-install is selected and parts exist. */}
      {isDeclineSelected && hasRequiredParts && (
        <div className="sui-flex sui-flex-col sui-gap-2 sui-mt-2">
          <Typography variant="body-base" weight="bold" role="heading">
            {INSTALL_REQUIRED_PARTS_TITLE({ isPlural: isPluralParts })}
          </Typography>
          <Typography variant="body-sm">{INSTALL_PARTS_WARRANTY_NOTE}</Typography>
          {installPricing.installPartItems.map((part) => {
            const isPartChecked = selectedPartConfigAttrs.includes(part.configAttr);
            return (
              <SelectionControlLabel
                key={part.configAttr}
                alignment="center"
                value={part.description}
                label={(
                  <span className="sui-flex sui-gap-1 sui-flex-wrap">
                    <Typography component="span">{part.description}</Typography>
                    <Typography component="span" weight="bold">{formatPrice(part.price)}</Typography>
                  </span>
                )}
              >
                <Checkbox
                  checked={isPartChecked}
                  name={part.description}
                  onChange={() => handlePartToggle(part.configAttr)}
                />
              </SelectionControlLabel>
            );
          })}
        </div>
      )}
    </div>
  );
};

TileButton.propTypes = {
  onSelectionChange: PropTypes.func,
  type: PropTypes.oneOf(['protection', 'installation', 'haulAway']).isRequired,
  userSelection: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object)
  ]),
  availableItems: PropTypes.arrayOf(PropTypes.shape({})),
  isBopisAndPickup: PropTypes.bool
};

TileButton.defaultProps = {
  onSelectionChange: null,
  userSelection: null,
  availableItems: [],
  isBopisAndPickup: false
};

TileButton.displayName = 'TileButton';
